import numeral from 'numeral';
import { BITCOIN_DECIMAL_PLACES_FORMAT, DEFAULT_DECIMAL_FORMAT } from '@shared/utils/number.utils';
import { format } from '@shared/utils/number.utils';

export class BitcoinRate {
  constructor() {
    this._latestBaseRate = null;
    this._conversionTable = [];
    this._config = null;
    this._type = 'deposit';
  }

  static fromPayload(payload) {
    const bitcoinRate = new BitcoinRate();
    
    bitcoinRate.latestBaseRate = numeral(payload.latest_base_rate);
    bitcoinRate.config = Config.fromPayload(payload['bitcoin_config']);
    
    payload.conversion_table.forEach(rangePayload => {
      bitcoinRate.conversionTable.push(Range.fromPayload(rangePayload, bitcoinRate.latestBaseRate))
    });

    return bitcoinRate;
  }

  static fromWithdrawalAdjustmentPayload(payload) {
    const bitcoinRate = new BitcoinRate();
    
    bitcoinRate.latestBaseRate = numeral(payload.latest_base_rate);
    bitcoinRate.config = WithdrawalConfig.fromPayload(payload['bitcoin_config']);
    
    payload.conversion_table.forEach(rangePayload => {
      bitcoinRate.conversionTable.push(Range.fromPayload(rangePayload, bitcoinRate.latestBaseRate))
    });

    bitcoinRate.type = 'withdrawal';

    return bitcoinRate;
  }

  set type(type) {
    this._type = type;
  }

  get type() {
    return this._type;
  }

  set latestBaseRate(latestBaseRate) {
    this._latestBaseRate = latestBaseRate;
  }

  get latestBaseRate() {
    return this._latestBaseRate;
  }
  
  set conversionTable(conversionTable) {
    this._conversionTable = conversionTable;
  }

  get conversionTable() {
    return this._conversionTable;
  }

  set config(config) {
    this._config = config;
  }

  get config() {
    return this._config;
  }

  get initialRate() {
    const range = this._getDefaultRange();

    return range.adjustRate(this.latestBaseRate, this.type).format(DEFAULT_DECIMAL_FORMAT);
  }
  
  /** 
   * @param {number} totalBtc the total btc's user has inputed
   * @returns string
   */
  computeAdjustedRate(totalBtc) {
    const range = this.getRange(totalBtc);

    return range.adjustRate(this.latestBaseRate, this.type).format(DEFAULT_DECIMAL_FORMAT);
  }

  computeAdjustedRateFromAmount(totalAmount) {
    const range = this._getRangeFromAmount(totalAmount);
    
    return range.adjustRate(this.latestBaseRate, this.type).format(DEFAULT_DECIMAL_FORMAT);
  }

  getRange(btc) {
    const inBetween = (number, start, end) => {
      return number >= start && number <= end;
    }

    const range = this.conversionTable.filter(range => {
      return inBetween(btc, range.start.value(), range.end.value());
    });

    if (range.length < 1) {
      return this._getDefaultRange();
    }

    return range[0];
  }

  _getRangeFromAmount(amount) {
    const inBetween = (number, start, end) => {
      return number >= start && number <= end;
    }

    const range = this.conversionTable.filter(range => {
      return inBetween(amount, range.startAmount.value(), range.endAmount.value());
    });

    if (range.length < 1) {
      return this._getDefaultRange();
    }

    return range[0];
  }

  _getDefaultRange() {
    return this.conversionTable.filter(range => range.isDefault)[0];
  }
}

export class Range {
  constructor() {
    this._start = null;
    this._end = null;
    this._isDefault = false;
    this._fixedAdjustment = numeral(0);
    this._percentAdjustment = numeral(0);

    // Used to convert actual amount into btc value.
    this._startAmount = null;
    this._endAmount = null;
  }

  static fromPayload(payload, latestBaseRate) {
    const range = new Range();

    range.isDefault = payload.is_default;
    range.start = numeral(payload.range_start);
    range.end = numeral(payload.range_end);
    range.fixedAdjustment = numeral(payload.fixed_adjustment);
    range.percentAdjustment = numeral(payload.percent_adjustment);

    const baseRate = range.adjustRate(latestBaseRate);
    range.startAmount = numeral(baseRate.value() * range.start.value());
    range.endAmount = numeral(baseRate.value() * range.end.value());

    return range;
  }

  set start(start) {
    this._start = start;
  }

  get start() {
    return this._start;
  }

  set end(end) {
    this._end = end;
  }

  get end() {
    return this._end;
  }

  set startAmount(startAmount) {
    this._startAmount = startAmount;
  }

  get startAmount() {
    return this._startAmount;
  }

  set endAmount(endAmount) {
    this._endAmount = endAmount;
  }

  setEndAmount(nextRange, baseRate) {
    const btcSmallestValue = numeral('0.0000000001');
    const smallestValue =  numeral(baseRate.value() * btcSmallestValue.value());
    this.endAmount = numeral(nextRange.startAmount.value() - smallestValue.value());
  }

  get endAmount() {
    return this._endAmount;
  }

  set isDefault(isDefault) {
    this._isDefault = isDefault;
  }

  get isDefault() {
    return this._isDefault;
  }

  set fixedAdjustment(fixedAdjustment) {
    this._fixedAdjustment = fixedAdjustment;
  }

  get fixedAdjustment() {
    return this._fixedAdjustment;
  }

  set percentAdjustment(percentAdjustment) {
    this._percentAdjustment = percentAdjustment;
  }

  get percentAdjustment() {
    return this._percentAdjustment;
  }

  static formatDecimal(numeral) {
    return format(numeral, BITCOIN_DECIMAL_PLACES_FORMAT);
  }

  /**
   * Computes for Adjusted rate.
   * 
   * 
   * @param numeral bitcoinLatestRate 
   * @returns numeral
   * @throws an error probably because the api returns erroneous payload.
   */
  adjustRate(bitcoinLatestRate, type) {
    if (this.fixedAdjustment.value() !== null) { 
      return this._computeByFixedAdjustment(bitcoinLatestRate, type);
    }

    if (this.percentAdjustment.value() !== null) {
      return this._computeByPercentageAdjustment(bitcoinLatestRate, type);
    }

    throw 'Code should not go here.';
  }
  
  _computeByFixedAdjustment(bitcoinLatestRate, type) {
    let adjustment = null;
    
    if (type === 'deposit') {
      adjustment = bitcoinLatestRate.value() - this.fixedAdjustment.value();
    } else {
      adjustment = bitcoinLatestRate.value() + this.fixedAdjustment.value();
    }

    return numeral(adjustment);
  }

  _computeByPercentageAdjustment(bitcoinLatestRate, type) {
    let adjustment = bitcoinLatestRate.value() * (this.percentAdjustment.value() / 100);

    if (type === 'deposit') {
      adjustment = numeral(bitcoinLatestRate.value() - adjustment);
    } else { 
      adjustment = numeral(bitcoinLatestRate.value() + adjustment);
    }

    return numeral(adjustment.format(BITCOIN_DECIMAL_PLACES_FORMAT));
  }

  get adjustmentType() {
    if (this.fixedAdjustment.value() !== null) { 
      return 'fixed';
    }

    if (this.percentAdjustment.value() !== null) {
      return 'percentage';
    }

    throw 'Code should not go here.';
  }

  // The value to be deducted based on adjustment type
  getAdjustment(bitcoinLatestRate) {
    if (this.adjustmentType === 'fixed') {
      return this.fixedAdjustment.value();
    }

    if (this.percentAdjustment.value() !== null) {
      return bitcoinLatestRate.value() * (this.percentAdjustment.value() / 100);;
    }

    throw 'Code should not go here.';
  }
}

export class Config {
  constructor(minimumAllowedDeposit, maximumAllowedDeposit, minutesInterval ) {
    this._minimumAllowedDeposit = numeral(minimumAllowedDeposit);
    this._maximumAllowedDeposit = numeral(maximumAllowedDeposit);
    this._minutesInterval = minutesInterval;
  }

  static fromPayload(payload) {
    const config = new Config(payload['minimumAllowedDeposit'], payload['maximumAllowedDeposit'], payload['minutesInterval']);

    return config;
  }

  set maximumAllowedDeposit(maximumAllowedDeposit) {
    this._maximumAllowedDeposit = maximumAllowedDeposit;
  }

  get maximumAllowedDeposit() {
    return this._maximumAllowedDeposit;
  }

  get formattedMaximumAllowedDeposit() {
    return format(this.maximumAllowedDeposit, BITCOIN_DECIMAL_PLACES_FORMAT);
  }

  set minimumAllowedDeposit(minimumAllowedDeposit) {
    this._minimumAllowedDeposit = minimumAllowedDeposit;
  }

  get minimumAllowedDeposit() {
    return this._minimumAllowedDeposit;
  }

  get minutesInterval() {
    return this._minutesInterval;
  }

  get formattedMinimumAllowedDeposit() {
    return format(this.minimumAllowedDeposit, BITCOIN_DECIMAL_PLACES_FORMAT);
  }
}

export class WithdrawalConfig {
  constructor(minimumAllowedWithdrawal, maximumAllowedWithdrawal, minutesWithdrawalInterval ) {
      this._minimumAllowedWithdrawal = numeral(minimumAllowedWithdrawal);
      this._maximumAllowedWithdrawal = numeral(maximumAllowedWithdrawal);
      this._minutesWithdrawalInterval = minutesWithdrawalInterval;
  }

  static fromPayload(payload) {
      const withdrawalConfig = new WithdrawalConfig(payload['minimumAllowedWithdrawal'], payload['maximumAllowedWithdrawal'], payload['minutesWithdrawalInterval']);

      return withdrawalConfig;
  }

  set maximumAllowedWithdrawal(maximumAllowedWithdrawal) {
      this._maximumAllowedWithdrawal = maximumAllowedWithdrawal;
  }

  get maximumAllowedWithdrawal() {
      return this._maximumAllowedWithdrawal;
  }

  get formattedMaximumAllowedWithdrawal() {
      return format(this.maximumAllowedWithdrawal, BITCOIN_DECIMAL_PLACES_FORMAT);
  }

  set minimumAllowedWithdrawal(minimumAllowedWithdrawal) {
      this._minimumAllowedWithdrawal = minimumAllowedWithdrawal;
  }

  get minimumAllowedWithdrawal() {
      return this._minimumAllowedWithdrawal;
  }

  get minutesWithdrawalInterval() {
      return this._minutesWithdrawalInterval;
  }

  get formattedMinimumAllowedWithdrawal() {
      return format(this.minimumAllowedWithdrawal, BITCOIN_DECIMAL_PLACES_FORMAT);
  }
}

export default BitcoinRate;



// WEBPACK FOOTER //
// ./client/app/api/models/bitcoin-rate.model.js